/**
 * Lógica derivada PURA del modal "Detalle de producto negado"
 * (`NegadosDrillModal`, rediseño 2026-07 — spec del usuario). Separada del
 * componente para poder testear agregación/relleno/coords sin montar React;
 * `cdr-sa-admin` porta este archivo VERBATIM (Task 2, espejo) salvo el path
 * de import de `NegadoDetalleRow`.
 *
 * Todo lo de acá es sobre CONTEOS de negados, nunca montos ($) — la regla del
 * proyecto no cambia por ser un mini-gráfico.
 */
import type { NegadoDetalleRow } from '../types/indicadores.types';

/** Un día agregado (ISO `YYYY-MM-DD`) + su total de negados. */
export interface DiaNegados {
  fecha: string;
  negados: number;
}

/** Una talla agregada + su total de negados. */
export interface TallaNegados {
  talla: string;
  negados: number;
}

/**
 * Suma `negados` de `filas` agrupando por `talla` (colapsa fechas/horas/
 * sucursales de cada talla en un solo total) y ordena por talla NUMÉRICA
 * ascendente (ej. `"7"` antes que `"26"`, no caracter a caracter) — preserva
 * el orden de la corrida. Fallback a `localeCompare` cuando la talla no es
 * numérica (ej. tallas con letras).
 */
export function aggregatePorTalla(filas: NegadoDetalleRow[]): TallaNegados[] {
  const totals = new Map<string, number>();
  for (const f of filas) {
    totals.set(f.talla, (totals.get(f.talla) ?? 0) + f.negados);
  }
  return Array.from(totals, ([talla, negados]) => ({ talla, negados })).sort((a, b) => {
    const na = Number(a.talla);
    const nb = Number(b.talla);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) {
      return na - nb;
    }
    return a.talla.localeCompare(b.talla, 'es');
  });
}

/**
 * Suma `negados` de `filas` agrupando por `fecha` (`YYYY-MM-DD`, colapsa
 * tallas/horas/sucursales de ese día) y ordena CRONOLÓGICAMENTE ascendente
 * (el formato `YYYY-MM-DD` con padding hace que el orden léxico coincida con
 * el cronológico). A diferencia del `aggregatePorDia` anterior, conserva el
 * ISO SIN formatear — lo necesita `serieContinua` para rellenar huecos.
 */
export function aggregatePorDiaRaw(filas: NegadoDetalleRow[]): DiaNegados[] {
  const totals = new Map<string, number>();
  for (const f of filas) {
    totals.set(f.fecha, (totals.get(f.fecha) ?? 0) + f.negados);
  }
  return Array.from(totals, ([fecha, negados]) => ({ fecha, negados })).sort((a, b) =>
    a.fecha.localeCompare(b.fecha),
  );
}

/**
 * Rellena los días FALTANTES entre el mínimo y el máximo de `porDia` con
 * `negados: 0` (eje continuo — antes el gráfico saltaba de 13/07 a 16/07 sin
 * mostrar que 14 y 15 no tuvieron negados). `[]` si `porDia` viene vacío.
 *
 * tz-safe A PROPÓSITO: itera en UTC (`new Date(iso+'T00:00:00Z')` +
 * `getUTCDate`/`setUTCDate` + `toISOString().slice(0,10)`), NUNCA
 * `new Date(iso)` + `getDate()` LOCAL — el spec de referencia usaba fechas
 * locales y es BUGGY cerca de medianoche/cambios de huso (Global Constraints
 * del plan). Con UTC, un ISO `YYYY-MM-DD` siempre hace roundtrip a sí mismo.
 */
export function serieContinua(porDia: DiaNegados[]): DiaNegados[] {
  if (porDia.length === 0) {
    return [];
  }
  const orden = [...porDia].sort((a, b) => a.fecha.localeCompare(b.fecha));
  const desde = new Date(`${orden[0].fecha}T00:00:00Z`);
  const hasta = new Date(`${orden[orden.length - 1].fecha}T00:00:00Z`);
  const mapa = new Map(orden.map((d) => [d.fecha, d.negados]));

  const out: DiaNegados[] = [];
  for (let d = new Date(desde); d.getTime() <= hasta.getTime(); d.setUTCDate(d.getUTCDate() + 1)) {
    const iso = d.toISOString().slice(0, 10);
    out.push({ fecha: iso, negados: mapa.get(iso) ?? 0 });
  }
  return out;
}

/** KPIs derivados de la franja superior del modal (`derivarKpis`). */
export interface DrillKpis {
  total: number;
  tallaCritica: TallaNegados;
  diaPico: DiaNegados;
  tallasAfectadas: number;
}

/**
 * Deriva los 4 KPIs de la franja superior a partir de `porTalla` (agregación
 * por talla) y `serie` (serie continua por día, ya con los huecos en 0):
 * - `total`: suma de `porTalla` (== suma de `serie`, es la misma data en 2 ejes).
 * - `tallaCritica`/`diaPico`: máximo por `reduce` — en empate gana el PRIMERO
 *   (criterio del spec, no el último).
 * - `tallasAfectadas`: cuántas tallas de `porTalla` tienen `negados > 0`.
 *
 * `null`-safe: si `porTalla` o `serie` vienen vacíos (sin filas en el
 * periodo) devuelve `null` en vez de reventar con `reduce` sobre `[]` — el
 * componente lo trata como el estado vacío.
 */
export function derivarKpis(porTalla: TallaNegados[], serie: DiaNegados[]): DrillKpis | null {
  if (porTalla.length === 0 || serie.length === 0) {
    return null;
  }
  const total = porTalla.reduce((s, t) => s + t.negados, 0);
  const tallaCritica = porTalla.reduce((a, b) => (b.negados > a.negados ? b : a));
  const diaPico = serie.reduce((a, b) => (b.negados > a.negados ? b : a));
  const tallasAfectadas = porTalla.filter((t) => t.negados > 0).length;
  return { total, tallaCritica, diaPico, tallasAfectadas };
}

/** Participación `n` sobre `total` en porcentaje entero 0..100 (`0` si `total<=0`). */
export function share(n: number, total: number): number {
  return total > 0 ? Math.round((n / total) * 100) : 0;
}

/** `YYYY-MM-DD` -> `DD/MM` por split del string (tz-safe, NUNCA vía `Date`). */
export function fmtDia(iso: string): string {
  const [, mm, dd] = iso.split('-');
  return `${dd}/${mm}`;
}

/** Dimensiones del `viewBox` del SVG de tendencia por día (spec §6). */
export const DRILL_VB = { w: 600, h: 150, padL: 12, padR: 12, baseY: 116, topY: 24 } as const;

/** Un punto de la tendencia por día, en coords del `viewBox` `DRILL_VB`. */
export interface PuntoTendencia {
  fecha: string;
  negados: number;
  x: number;
  y: number;
}

/** Geometría SVG completa de "Negados por día" (puntos + path de línea + path de área). */
export interface Tendencia {
  pts: PuntoTendencia[];
  /** Puntos `"x,y"` separados por espacio, para el `points` de un `<polyline>`. */
  linea: string;
  /** `d` de un `<path>` de área cerrada (baseline -> serie -> baseline -> Z). */
  area: string;
  /** Máximo de la serie usado para escalar `y` (mínimo `1`, para no dividir entre 0). */
  maxV: number;
}

/**
 * Calcula las coordenadas SVG de la tendencia por día a partir de `serie`
 * (ya continua, con los huecos en 0) — spec §6. `x` reparte los puntos a lo
 * ancho del plot (`n===1` centra el único punto, no lo pega al borde
 * izquierdo); `y` escala `negados` contra `maxV` (mínimo 1 para series todo
 * en 0). Guard sobre `serie` vacío (devuelve geometría vacía, sin dividir
 * entre `n-1===−1`).
 */
export function tendenciaPuntos(serie: DiaNegados[]): Tendencia {
  const { padL, padR, baseY, topY, w } = DRILL_VB;
  const plotW = w - padL - padR;
  const plotH = baseY - topY;
  const n = serie.length;

  if (n === 0) {
    return { pts: [], linea: '', area: '', maxV: 1 };
  }

  const maxV = Math.max(...serie.map((s) => s.negados), 1);
  const pts: PuntoTendencia[] = serie.map((s, i) => ({
    fecha: s.fecha,
    negados: s.negados,
    x: n === 1 ? padL + plotW / 2 : padL + (i / (n - 1)) * plotW,
    y: baseY - (s.negados / maxV) * plotH,
  }));

  const linea = pts.map((p) => `${p.x},${p.y}`).join(' ');
  const area = `M${pts[0].x},${baseY} ${linea} ${pts[pts.length - 1].x},${baseY} Z`;

  return { pts, linea, area, maxV };
}
